import React, { useContext, useEffect, useMemo, useState } from "react";
import BaseWrapper from "../../components/ui/BaseWrapper";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../utils/variables";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import routes from "../../routes";
import { LayoutContext } from "../../context/LayoutContext";
import { useQuery } from "@tanstack/react-query";
import { me } from "../../services/api/user";
import useAuth from "../../hooks/useAuth";
import { fetchQualificationTypes } from "../../services/api/qualification";
import { isVendorTech } from "../../utils/helpers";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "38px",
  width: "230px",
  fontWeight: 300,
  fontSize: "16px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",

  borderRadius: "6px",
  // padding: 0,
  margin: "20px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&:hover": {
    /* color: "#40a9ff",
          opacity: 1, */
  },
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },
  "&.Mui-focusVisible": {
    //   backgroundColor: "#d1eaff",
  },
  // Declared last so these win over the base values above on small screens.
  [theme.breakpoints.down("sm")]: {
    width: "auto",
    minWidth: "160px",
    height: "44px",
    padding: "0 22px",
    fontSize: "16px",
    margin: "10px 0",
  },
}));

const ProfileSettings = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [value, setValue] = useState(0);
  const { authRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isPending: isUserLoading,
    error: isUserError,
    data: userData,
    refetch: refetchMe,
  } = useQuery({
    queryKey: ["userData"],
    queryFn: me,
    retry: 2,
  });

  const {
    isLoading: isQualificationsTypesLoading,
    error: isQualificationsTypesError,
    data: qualificationsTypesData,
  } = useQuery({
    queryKey: ["qualificationsTypes"],
    queryFn: fetchQualificationTypes,
  });

  const qualificationTypeMap = useMemo(() => {
    if (!qualificationsTypesData?.data?.data) return {};
    return qualificationsTypesData?.data?.data?.reduce((acc, item) => {
      acc[item.name.toLowerCase()] = item.id;
      return acc;
    }, {});
  }, [qualificationsTypesData]);

  const userProfileSettingsRoute = routes.find((route) => route.path === "/user/settings");

  const allTabs = userProfileSettingsRoute?.childs || [];

  const visibleTabs = allTabs.filter((tab) => {
    if (!tab?.roles?.includes(authRole)) return false;
    if (
      tab.path.startsWith("linkedCompanies") &&
      !userData?.isCompanyMember &&
      !userData?.hasPendingCompanyInvite
    )
      return false;
    // A vendor tech is paid by their own vendor, so they hold no bank account here.
    if (tab.path.startsWith("bankAccounts") && isVendorTech(userData)) return false;
    return true;
  });

  useEffect(() => {
    const currentTabIndex = visibleTabs?.findIndex((tab) =>
      location.pathname.endsWith(`/user/settings/${tab.path.replace(":id", userData?.id)}`),
    );

    if (currentTabIndex !== -1) {
      setValue(currentTabIndex);
    }
  }, [location.pathname, visibleTabs]);

  const handleChange = (event, newValue) => {
    const selectedTab = visibleTabs[newValue];

    navigate(`/user/settings/${selectedTab.path.replace(":id", userData?.id)}`);
    setValue(newValue);
  };

  return (
    <div className="bg-light h-full">
      <div className={`relative ${isBannerVisible ? "top-28" : "top-14"}`}>
        <div className="border-b border-zinc-300 bg-white">
          <BaseWrapper>
            <div className="py-[7px]">
              <h3 className="font-light">Profile Settings</h3>
            </div>
          </BaseWrapper>
        </div>
        <BaseWrapper>
          <Tabs
            TabIndicatorProps={{
              style: {
                display: "none", // Hide the indicator
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              // The global `.MuiTabs-flexContainer` overflow rule can add a second
              // scrollbar inside the scrollable variant - let MUI own it.
              "& .MuiTabs-flexContainer": { overflow: "visible !important" },
              // A disabled scroll button still reserves width, which indents the
              // first tab away from the page edge.
              "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0, opacity: 0 },
            }}
          >
            {visibleTabs?.map(
              (tab, index) =>
                tab?.roles.includes(authRole) && (
                  <CustomTab
                    key={index}
                    label={tab.text}
                    {...a11yProps(index)}
                    sx={{
                      minHeight: "38px",
                      marginRight: { xs: "12px", sm: "40px" },
                    }}
                  />
                ),
            )}
          </Tabs>

          {visibleTabs?.map((tab, index) => {
            if (!tab?.roles?.includes(authRole)) return null;
            return (
              <CustomTabPanel value={value} index={index} key={index}>
                {React.cloneElement(tab.component, {
                  user: userData,
                  refetchMe,
                  qualificationTypeMap,
                })}
              </CustomTabPanel>
            );
          })}
        </BaseWrapper>
      </div>
    </div>
  );
};

export default ProfileSettings;
